import { useQuery } from '@tanstack/react-query';

/** Source folders under public/legal/content/ (see tools/build-legal-docs.mjs). */
export type TLegalContentType =
    | 'eula'
    | 'privacy-policy'
    | 'legal-notice'
    | 'eifu'
    | 'professional-attestation';

async function fetchLegalContent(type: TLegalContentType, locale: string): Promise<string> {
    const response = await fetch(`/legal/content/${type}/${locale}.html`);
    if (!response.ok) {
        throw new Error(`Failed to load legal content ${type}/${locale} (HTTP ${response.status})`);
    }
    const body = await response.text();
    // The build emits HTML *fragments* (no document shell). A full document here
    // means an SPA-fallback rewrite served index.html for a missing artifact —
    // fail rather than render the app shell as legal prose (a user must never be
    // able to "accept" a document that did not display).
    if (/<!doctype\s|<html[\s>]/i.test(body)) {
        throw new Error(`Legal content ${type}/${locale} is missing (SPA fallback served instead)`);
    }
    return body;
}

/**
 * Loads a pre-rendered legal document HTML fragment (static asset generated
 * from `legal/` by the legal docs build). Content is immutable per deployment,
 * so it never goes stale within a session.
 */
export function useLegalContent(type: TLegalContentType, locale: string) {
    return useQuery({
        queryKey: ['legal-content', type, locale],
        queryFn: () => fetchLegalContent(type, locale),
        staleTime: Infinity,
    });
}
